import React from 'react';
import { LockedIcon, UnlockedIcon } from 'assests/GmsIcons';
import { FOCUS_RING } from 'Components/DesignSystem/focusRing';

type TLockSwitchProps = {
    metric: string;
    isLocked: boolean;
    disabled?: boolean;
    toggleLock: (metric: string) => void;
    'data-testid'?: string;
}

/**
 * The Score Weightage per-metric lock (`okf/design-system/e2e.md` § 145).
 *
 * A wordless icon button with no accessible name, no focus indicator, no `type`, and a
 * className expression that did not do what it reads as:
 *
 * ```
 * className={`focus:tw-outline-none` + !isLocked && disabled ? "tw-opacity-20" : ""}
 * ```
 *
 * `+` binds tighter than `&&`, so this is
 * `("focus:tw-outline-none" + !isLocked) && disabled ? … : …`. The left operand is a
 * non-empty string, always truthy, so the whole condition collapses to **`disabled`** —
 * the `!isLocked` half never mattered, and `focus:tw-outline-none` was never emitted as
 * a class at all. Net effect: the button faded when disabled (the intended outcome,
 * reached by accident) and had no focus treatment either way.
 *
 * `aria-pressed` is the substantive addition: a toggle button that changes an icon and
 * nothing else is invisible to a screen reader, and there is no visible text tying the
 * control to the metric row it belongs to.
 *
 * Deliberately NOT a DS `Toggle`. A switch is a labelled row control; this is a 20px
 * icon inside a grid cell, and the icons (padlock open / closed) are the established
 * vocabulary here and in ISR. It stays a button, correctly built.
 */
const LockSwitch = ({ metric, isLocked, disabled, toggleLock, 'data-testid': dataTestId }: TLockSwitchProps) => {
    return (
        <div className="tw-flex tw-items-center tw-justify-center">
            <button
                type="button"
                data-testid={dataTestId ?? "components-lockswitch-button-1"}
                disabled={disabled}
                aria-pressed={isLocked}
                aria-label={isLocked ? `Unlock ${metric} weighting` : `Lock ${metric} weighting`}
                onClick={() => { if (!disabled) toggleLock(metric) }}
                className={`tw-rounded-ctl tw-p-0.5 ${FOCUS_RING} ${disabled ? 'tw-opacity-20 tw-cursor-not-allowed' : 'tw-cursor-pointer'}`}
            >
                {isLocked ? (<LockedIcon />) : (<UnlockedIcon />)}
            </button>
        </div>
    );
}

export default LockSwitch;
